import type { ConferenceEvent, Session } from '@conference/shared';

type EventClock = {
  date: string;
  minutes: number;
};

const NON_DISPLAYABLE_SESSION_STATUSES = new Set(['cancelled', 'ended', 'completed']);

function eventClock(now: Date, timeZone: string): EventClock {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  return {
    date: `${values.year}-${values.month}-${values.day}`,
    minutes: Number(values.hour) * 60 + Number(values.minute),
  };
}

function minutesSinceMidnight(value: string): number {
  const [hours = '0', minutes = '0'] = value.split(':');
  return Number(hours) * 60 + Number(minutes);
}

function isWithinEvent(session: Session, event: ConferenceEvent): boolean {
  return session.date >= event.date_start && session.date <= event.date_end;
}

function isDisplayable(session: Session): boolean {
  return !NON_DISPLAYABLE_SESSION_STATUSES.has(session.status);
}

/** Derive live and next sessions from the official event timezone, not stored labels. */
export function getScheduleState(
  sessions: Session[],
  event: ConferenceEvent,
  now = new Date(),
): { live: Session[]; upcoming: Session[] } {
  const clock = eventClock(now, event.timezone || 'UTC');
  if (clock.date < event.date_start || clock.date > event.date_end) {
    return { live: [], upcoming: [] };
  }
  const scheduled = sessions
    .filter((session) => isWithinEvent(session, event) && isDisplayable(session))
    .sort((left, right) => (
      left.date.localeCompare(right.date)
      || left.start_time.localeCompare(right.start_time)
    ));

  return {
    live: scheduled.filter((session) => (
      session.date === clock.date
      && minutesSinceMidnight(session.start_time) <= clock.minutes
      && clock.minutes < minutesSinceMidnight(session.end_time)
    )),
    upcoming: scheduled.filter((session) => (
      session.date > clock.date
      || (session.date === clock.date && minutesSinceMidnight(session.start_time) > clock.minutes)
    )).slice(0, 2),
  };
}

function parseCalendarDate(value: string): { year: number; month: number; day: number } {
  const [year, month, day] = value.split('-').map(Number);
  return { year, month, day };
}

function monthName(year: number, month: number): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, 1)));
}

/** Format ISO calendar dates without converting them through the device timezone. */
export function formatEventDateRange(start: string, end: string): string {
  const first = parseCalendarDate(start);
  const last = parseCalendarDate(end);
  const firstMonth = monthName(first.year, first.month);
  const lastMonth = monthName(last.year, last.month);
  if (first.year === last.year && first.month === last.month) {
    return first.day === last.day
      ? `${firstMonth} ${first.day}, ${first.year}`
      : `${firstMonth} ${first.day}\u2013${last.day}, ${first.year}`;
  }
  if (first.year === last.year) {
    return `${firstMonth} ${first.day} \u2013 ${lastMonth} ${last.day}, ${first.year}`;
  }
  return `${firstMonth} ${first.day}, ${first.year} \u2013 ${lastMonth} ${last.day}, ${last.year}`;
}
